// Angular
import { Injectable } from '@angular/core';

// RXJS
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

// NGRX & state dependencies
import {Store} from '@ngrx/store';
import * as actions from '../../../actions';
import * as fromRoot from '../../../reducers';

// Miscellaneous
import * as _ from 'lodash';
import {
  DocumentTravelPayload,
  ValidTravelDocumentListResponse,
  ValidTravelGuest
} from '../../../models';
import moment from 'moment/moment';
import { constants } from '../../../constants';
import { TravelDocumentEnum } from '../../../models/enums';
import { TravelDocsListPayload } from '../../../models/guest-details/travel-docs-list-payload';

// Services
import { MicroServicesService } from './micro-services.service';

@Injectable({
  providedIn: 'root'
})
export class TravelDocumentsService {
  constructor(
    private readonly store: Store<fromRoot.AppState>,
    public readonly microServicesService: MicroServicesService
  ) {}

  retrieveDocumentTypesPayload(documentTravelPayload: DocumentTravelPayload[]) {
    return { guests: documentTravelPayload };
  }

  public createTravelDocumentsPayload(beginDetailsResponse: any) {
    const documentTypeRequestList: any = [];
    const bookingsGuestsInformation = _.get(beginDetailsResponse, 'payload.bookings', []);

    if (!bookingsGuestsInformation.length) {
      return documentTypeRequestList;
    }

    bookingsGuestsInformation.forEach((booking: any) => {
      const guests = booking.guests;
      if (!guests.length) {
        return documentTypeRequestList;
      }
      guests.forEach((guest: any) => {
        documentTypeRequestList.push(
          this.retrieveDocumentTypesArray(guest, booking.bookingId)
        );
      });
    });

    return this.retrieveDocumentTypesPayload(documentTypeRequestList);
  }

  public getTravelDocuments(beginDetailsResponse: any): Observable<any> {
    if (!beginDetailsResponse) {
      return of(null);
    }
    const documentTypesPayload = this.createTravelDocumentsPayload(beginDetailsResponse);
    return this.microServicesService.retrieveValidTravelDocumentList(documentTypesPayload).pipe(
      map(travelDocumentsResponse => {
        if (
          this.retrieveEmptyTravelValidation(travelDocumentsResponse)
        ) {
          return of(null);
        }

        return travelDocumentsResponse;
      }),
      catchError(error => {
        return of(error);
      })
    );
  }

  public updateGuestTravelDocs(params: TravelDocsListPayload) {
    const newParams = this.retrieveOneRecordPayload(params);
    this.store.dispatch(new actions.LoadGuestDocumentTypesAction(newParams));
  }

  retrieveDocumentTypesArray(
    guest: any, bookingId: string | number
  ): DocumentTravelPayload {
    const immigrationInfo = guest.immigrationInfo;
    const citizenship = immigrationInfo.countryOfCitizenship;
    const birthdate = moment(guest.personalInfo.birthdate, constants.DATE_FORMAT.MICROSERVICE).format(constants.DATE_FORMAT.SERVER);

    return {
      guestId: guest.guestId.toString(),
      bookingId: bookingId.toString(),
      birthdate: this.replaceDateSlashes(birthdate),
      countryOfCitizenship: citizenship
    } as ValidTravelGuest;
  }

  retrieveEmptyTravelDocuments(
    travelDocumentsResponse:  ValidTravelDocumentListResponse
  ): { type: TravelDocumentEnum, emptyTravelDocuments: any[], status: boolean } {
    const isPayloadAppeared = this.validateObjectKey('payload', travelDocumentsResponse);

    if (isPayloadAppeared) {
      // @ts-ignore
      const emptyTravelDocuments = travelDocumentsResponse.payload?.guests;
      const status = travelDocumentsResponse.status !== constants.HTTP_STATUS_CODE.SUCCESS;
      return { type: TravelDocumentEnum.LIST, emptyTravelDocuments, status }
    }

    return { type: TravelDocumentEnum.DEFAULT, emptyTravelDocuments: [], status: false };
  }

  // Validation if documentTypeList is not empty and status is unsuccessful
  retrieveEmptyTravelValidation(
    travelDocumentsResponse: ValidTravelDocumentListResponse
  ): boolean {
    const { emptyTravelDocuments, status } = this.retrieveEmptyTravelDocuments(travelDocumentsResponse);

    const isEmptyTravelDocuments = emptyTravelDocuments.length === 0;
    return isEmptyTravelDocuments || status;
  }

  retrieveValidTravelDocuments(
    emptyTravelDocuments: any[],
    guestId: string | number
  ) {
    const guestTravelInfo = emptyTravelDocuments.find((travelInfo: any) => travelInfo.guestId === guestId);

    return guestTravelInfo.validTravelDocuments;
  }

  // Function to build the payload to get the new travel valid documents
  retrieveOneRecordPayload(params: TravelDocsListPayload) {
    return {
        type: TravelDocumentEnum.LIST,
        guests: [{
          guestId: params.guestId.toString(),
          bookingId: (params?.bookingId ?? '').toString(),
          birthdate: this.replaceDateSlashes(params.dob),
          countryOfCitizenship: params.countryOfCitizenship
        }]
      };
  }

  validateObjectKey(key: string, obj: any): boolean {
    if (!obj) {
      return false;
    }
    return key in obj;
  }

  replaceDateSlashes(birthDate: string) {
    return birthDate.split('-').join('');
  }
}
